/**
 * The web player's view of the server's build verdict, pushed over the
 * realtime gateway as a `client-advisory` frame (api-server
 * realtime/device-handlers.ts, policy in lib/client-release.ts).
 *
 * The advisory is advice, never a command. The strongest thing the server can
 * say is "this exact build is broken", and even then the player keeps playing:
 * a venue that went silent over a version check is a worse outage than the bug
 * the advisory is about. On the web the honest action is a reload, because
 * that is how a browser build updates.
 *
 * Semantics deliberately mirror the native shells' shared reader
 * (`@workspace/player-shell` client-release.ts) frame for frame, including
 * reading an unrecognised severity as "ok": a newer server's new severity must
 * never make an older player escalate for a reason it cannot interpret. This
 * is a local copy rather than an import because that module is only reachable
 * through the package's "." entry, which pulls the generated API client and so
 * cannot be loaded by the Node test runner. Collapsing the two is a one-line
 * change once player-shell exports `./client-release` as a subpath.
 */

export const ADVISORY_SEVERITIES = [
  "ok",
  "recommended",
  "required",
  "blocked",
] as const;
export type ClientAdvisorySeverity = (typeof ADVISORY_SEVERITIES)[number];

export interface ClientAdvisory {
  severity: ClientAdvisorySeverity;
  /** Server's machine reason ("below-minimum", "blocked-update", …). */
  reason: string | null;
  minSupportedVersion: string | null;
  recommendedVersion: string | null;
  /** Feature keys this build must treat as switched off fleet-wide. */
  disabledFeatures: string[];
  message: string | null;
  updateUrl: string | null;
}

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim().length > 0 ? v.trim() : null;
}

function severityOf(v: unknown): ClientAdvisorySeverity {
  const raw = str(v);
  return raw && (ADVISORY_SEVERITIES as readonly string[]).includes(raw)
    ? (raw as ClientAdvisorySeverity)
    : "ok";
}

function features(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  for (const entry of v) {
    const key = str(entry);
    if (key && !out.includes(key)) out.push(key);
  }
  return out;
}

/**
 * Read the `advisory` payload of a `client-advisory` frame. Null both for an
 * explicit clear (the owner removed the policy, which is how a killed feature
 * comes back without touching the venue) and for a frame that carries no
 * advisory object at all.
 */
export function readClientAdvisory(frame: unknown): ClientAdvisory | null {
  if (typeof frame !== "object" || frame === null) return null;
  const msg = frame as Record<string, unknown>;
  if (msg["type"] !== "client-advisory") return null;
  const raw = msg["advisory"];
  if (typeof raw !== "object" || raw === null) return null;
  const a = raw as Record<string, unknown>;
  return {
    severity: severityOf(a["severity"]),
    reason: str(a["reason"]),
    minSupportedVersion: str(a["minSupportedVersion"]),
    recommendedVersion: str(a["recommendedVersion"]),
    disabledFeatures: features(a["disabledFeatures"]),
    message: str(a["message"]),
    updateUrl: str(a["updateUrl"]),
  };
}

/**
 * True when the advisory is worth putting in front of an operator. A plain
 * "ok" verdict only reaches a client to carry a feature kill switch, so it is
 * shown only when it actually names one.
 */
export function advisoryIsNotable(advisory: ClientAdvisory | null): boolean {
  if (!advisory) return false;
  return advisory.severity !== "ok" || advisory.disabledFeatures.length > 0;
}
